import { DefaultSeo } from 'next-seo';
import { Jost } from 'next/font/google';
import { Router } from 'next/router';
import { useReportWebVitals } from 'next/web-vitals';
import React, { useEffect } from 'react';
import { CookiesProvider } from 'react-cookie';
import { SWRConfig } from 'swr';

import AppContainer from '../components/layout/AppContainer';
import { GoogleAnalytics } from '../lib/analytics/gtag';
import defaultSeoConfig from '../lib/seo/defaultSeoConfig';
import JsonLd from '../lib/seo/jsonLd';
import organizationJsonLd from '../lib/seo/organizationSeoConfig';
import { getSoftwareApplicationJsonLd, getWebPageJsonLd, webSiteJsonLd } from '../lib/seo/siteGraphSeoConfig';
import ErrorBoundaryRenderer from '../renderers/ErrorBoundaryRenderer';
import '../styles/tailwind.css';
import { DefaultPageStaticProps, NextPageWithLayout, NextPageWithLayoutDefaultPageProps } from '../types/global-types';

const reportableMetrics = ['Next.js-hydration', 'Next.js-route-change-to-render', 'Next.js-render'];

const jostFont = Jost({
  subsets: ['latin'],
  variable: '--font-jost',
  style: ['normal', 'italic'],
  weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
});

export const apiFetcher = (resource: string, init?: RequestInit) => {
  return fetch(resource, init).then((res) => res.json());
};

type ApplicationRootProps = {
  Component: NextPageWithLayout<NextPageWithLayoutDefaultPageProps>;
  router: Router;
  pageProps: DefaultPageStaticProps;
};
const ApplicationRoot: React.FC<ApplicationRootProps & any> = ({ Component, pageProps, router }) => {
  const CastedComponent = Component as any;
  // Emitted centrally rather than per page: router.pathname is the file route, which is exactly
  // what pageRoutes holds, so every static page is covered without touching thirty page files.
  const webPageJsonLd = getWebPageJsonLd(router.pathname);
  const softwareApplicationJsonLd = getSoftwareApplicationJsonLd(router.pathname);

  useReportWebVitals((metric) => {
    if (!reportableMetrics.includes(metric)) {
      return;
    }
    const logEndpoint = '/api/log-info';
    const body = `NextJS WebVitals: ${JSON.stringify(metric)}`;
    const options = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true };
    return !!navigator.sendBeacon ? navigator.sendBeacon(logEndpoint, body) : fetch(logEndpoint, options);
  });

  useEffect(() => {
    const handleRouteChange = (url: string) => GoogleAnalytics.trackPageView(url);
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <CookiesProvider cookies={Component.universalCookies}>
      <ErrorBoundaryRenderer>
        <DefaultSeo {...defaultSeoConfig} />
        <JsonLd id="organization" data={organizationJsonLd} />
        <JsonLd id="website" data={webSiteJsonLd} />
        {webPageJsonLd && <JsonLd id="webpage" data={webPageJsonLd} />}
        {softwareApplicationJsonLd && <JsonLd id="software-application" data={softwareApplicationJsonLd} />}
        <SWRConfig value={{ fetcher: apiFetcher }}>
          <AppContainer className={`${jostFont.variable} font-sans`}>
            <CastedComponent {...pageProps} />
          </AppContainer>
        </SWRConfig>
      </ErrorBoundaryRenderer>
    </CookiesProvider>
  );
};
export default ApplicationRoot;
